import type { AccountsListParams } from '@/api/accounts';
import type { SchedulerRunsParams } from '@/api/scheduler';
import type {
  EfficiencyParams,
  EvolutionParams,
  LinkReadinessParams,
  PageParams,
  RetirementCandidatesParams,
  TemplatesListParams,
  TopAccountsParams,
} from '@/types/api';
import type { Granularity } from '@/types/enums';

export const workspaceSettingsKeys = {
  all: ['workspace-settings'] as const,
};

export const accountKeys = {
  all: ['accounts'] as const,
  lists: () => [...accountKeys.all, 'list'] as const,
  list: (params: AccountsListParams) => [...accountKeys.lists(), params] as const,
  details: () => [...accountKeys.all, 'detail'] as const,
  detail: (id: string) => [...accountKeys.details(), id] as const,
  dashboard: (id: string, periodDays: number) =>
    [...accountKeys.all, 'dashboard', id, periodDays] as const,
  reels: (id: string, params: PageParams) => [...accountKeys.all, 'reels', id, params] as const,
  health: () => [...accountKeys.all, 'health'] as const,
};

export const identityKeys = {
  all: ['identities'] as const,
  list: () => [...identityKeys.all, 'list'] as const,
  detail: (id: string) => [...identityKeys.all, 'detail', id] as const,
  performance: (periodDays: number) => [...identityKeys.all, 'performance', periodDays] as const,
};

export const analyticsKeys = {
  all: ['analytics'] as const,
  conversions: (periodDays: number, identityId?: string) =>
    [...analyticsKeys.all, 'conversions', periodDays, identityId ?? null] as const,
  insights: (periodDays: number) => [...analyticsKeys.all, 'insights', periodDays] as const,
  overview: (periodDays: number) => [...analyticsKeys.all, 'overview', periodDays] as const,
  overviewSeries: (periodDays: number, granularity: Granularity) =>
    [...analyticsKeys.all, 'overview-series', periodDays, granularity] as const,
  topAccounts: (params: TopAccountsParams) =>
    [...analyticsKeys.all, 'top-accounts', params] as const,
  viewsEvolution: (params: EvolutionParams) =>
    [...analyticsKeys.all, 'views-evolution', params] as const,
  followersEvolution: (params: EvolutionParams) =>
    [...analyticsKeys.all, 'followers-evolution', params] as const,
  viralReels: (periodDays: number, limit: number) =>
    [...analyticsKeys.all, 'viral-reels', periodDays, limit] as const,
  linkReadiness: (params: LinkReadinessParams) =>
    [...analyticsKeys.all, 'link-readiness', params] as const,
  retirementCandidates: (params: RetirementCandidatesParams) =>
    [...analyticsKeys.all, 'retirement-candidates', params] as const,
  efficiency: (params: EfficiencyParams) => [...analyticsKeys.all, 'efficiency', params] as const,
};

export const templateKeys = {
  all: ['templates'] as const,
  lists: () => [...templateKeys.all, 'list'] as const,
  list: (params: TemplatesListParams) => [...templateKeys.lists(), params] as const,
  details: () => [...templateKeys.all, 'detail'] as const,
  detail: (hash: string, periodDays: number) =>
    [...templateKeys.details(), hash, periodDays] as const,
};

export const schedulerKeys = {
  all: ['scheduler'] as const,
  status: () => [...schedulerKeys.all, 'status'] as const,
  runs: (params: SchedulerRunsParams) => [...schedulerKeys.all, 'runs', params] as const,
  accountsHealth: () => [...schedulerKeys.all, 'accounts-health'] as const,
};
